/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import {useState} from 'react'
import {Controller, FieldErrors, useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import clsx from 'clsx'
import {toast} from 'utils/toastUtils'
import {LeadNote} from 'types/entities/LeadNote'
import {LeadNoteType} from 'types/enums/LeadNoteType'
import CompanyFormInput from 'pages/Front/Booking/components/CompanyForm/CompanyFormInput'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormGrid from 'components/Forms/UiFormGrid'
import {useAppDispatch} from 'helpers/store'
import {zodResolver} from '@hookform/resolvers/zod'
import {LeadNoteInputs, useLeadNoteSchema} from 'components/leads/schemas/useLeadNoteSchema'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import UiFormSelectField from 'components/Forms/UiFormSelectField'
import {closeSecondaryModal} from 'helpers/actions'
import {optionsFromEnum} from 'utils/optionsUtils'
import {Lead} from 'types/entities/Lead'
import {useLeadContext} from 'utils/hooks/useLeadContext'

interface LeadNoteFormInputs {
    onChange: () => void
    note?: LeadNote
    lead: Lead
}

const LeadNoteForm = (props: LeadNoteFormInputs) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const { actions: action } = useLeadContext(props.lead.category)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [workingNoteId, setWorkingNoteId] = useState<string | undefined>(props.note?.id)

    const LeadNoteSchema = useLeadNoteSchema()
    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<LeadNoteInputs>({
        resolver: zodResolver(LeadNoteSchema),
        defaultValues: {
            type: props.note?.type || LeadNoteType.Note,
            content: props.note?.content || '',
        },
    })

    const onError = (error: FieldErrors) => {
        console.log(error)
        toast.error(t('Please fix validation errors'))
    }

    const onSubmit = async (data: LeadNoteInputs, shouldClose: boolean = true) => {
        setIsSubmitting(true)
        try {
            const payload = {
                id: workingNoteId,
                lead_id: props.lead.id,
                type: data.type || LeadNoteType.Note,
                seller_id: props.lead.seller_id,
                content: data.content,
            } as unknown as Partial<LeadNote>

            const response = await dispatch(action.fetchCreateLeadNote(payload))
            toast.success(t('Note saved'))
            if (response?.note) {
                // Update the working note ID so subsequent saves use the correct ID
                setWorkingNoteId(response.note.id)
                if (props.onChange) {
                    props.onChange()
                }
            }
            if (shouldClose) {
                dispatch(closeSecondaryModal())
            }
        } catch (e: any) {
            let message = t('Failed to save note')
            const messages = e?.data?.messages ?? e?.response?.data?.messages ?? e?.messages
            if (messages && typeof messages === 'object') {
                const firstKey = Object.keys(messages)[0]
                const firstVal = (messages as any)[firstKey]
                if (Array.isArray(firstVal) && firstVal.length > 0) {
                    message = firstVal[0]
                }
            } else if (typeof e?.message === 'string') {
                message = e.message
            }
            toast.error(message)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <form
            onSubmit={handleSubmit((data) => onSubmit(data, true))}
            className={clsx('LeadNoteForm flex flex-col grow')}
        >
            <UiFormInner className={"p-2"}>
                <UiFormGrid cols={12} gap={6} responsive={false} className={clsx('grid-cols-12')}>
					<div className={"col-span-6 flex-col flex"}>
						<label className={"font-medium text-gray-700 dark:text-gray-200 text-sm pb-2 block select-none"}>{t("New note")}</label>
						<span className={"text-gray-500 tracking-wider text-sm"}>{t("Write a note to remind yourself about this lead")}</span>
					</div>
					<Controller
                        control={control}
                        name="type"
                        render={({ field, fieldState }) => (
                            <UiFormSelectField
                                className={clsx('col-span-6')}
                                label={t('Type')}
                                options={optionsFromEnum(LeadNoteType)}
                                name={field.name}
                                value={field.value}
                                onChange={field.onChange}
                                error={fieldState.error?.message}
                            />
                        )}
                    />

                    <CompanyFormInput
                        className={clsx('col-span-12')}
                        label={t('Content')}
                        name="content"
                        component="textarea"
                        register={register}
                        errors={errors}
                        placeholder={t('Write a note...')}
                    />
                </UiFormGrid>
            </UiFormInner>

            <UiFormActionsBar
                primaryText={isSubmitting ? t('Saving note...') : t('Save')}
                onPrimary={handleSubmit((data) => onSubmit(data, false))}
                saveAndCloseText={isSubmitting ? t('Saving note...') : t('Save & Close')}
                onSaveAndClose={handleSubmit((data) => onSubmit(data, true))}
                loading={isSubmitting}
            />
        </form>
    )
}

export default LeadNoteForm
